import React from 'react';
import { Chart } from 'react-chartjs-2';
import { useEffect, useRef, useState } from 'react';
import 'chart.js/auto';
import {
  ChartDataset,
  ChartType,
  ChartTypeRegistry,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';
import { MortgageProps } from '../contexts/MortgageContextProvider';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChartSimple } from '@fortawesome/free-solid-svg-icons'

type ChildProps = {
  labels: string[];
  datasets: MortgageProps[];
  startDate: string;
};

function ChartModeller({ labels, datasets, startDate }: ChildProps) {
  const [chartType, setChartType] = useState<ChartType>('line');
  const chartRef = useRef<ChartJSOrUndefined | null>(null);
  const [timeScale, setTimeScale] = useState<string>('monthly');
  const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'GBP', })

  const chartTypeOptions = [
    {
      label: 'Line Chart',
      value: 'line',
    },
    {
      label: 'Bar Chart',
      value: 'bar',
    },
  ];

  const timeScaleOptions = [
    {
      label: 'Monthly',
      value: 'monthly',
    },
    {
      label: 'Yearly',
      value: 'yearly',
    },
  ];

  const backgroundPlugin = {
    id: 'backgroundPlugin',
    beforeDraw: (chartCtx: any) => {
      const ctx = chartCtx.canvas.getContext('2d');
      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = 'rgba(249,247,245, 0)';
      ctx.fillRect(0, 0, chartCtx.width, chartCtx.height);
      ctx.restore();
    },
  };

  const hoverLinePlugin = {
    id: 'hoverLinePlugin',
    afterDraw: (chart: any) => {
      if (chart.tooltip._active && chart.tooltip._active.length && chart.config.type === 'line') {
        let x = chart.tooltip._active[0].element.x;
        let yAxis = chart.scales.y;
        let ctx = chart.ctx;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, yAxis.top);
        ctx.lineTo(x, yAxis.bottom);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(0, 0, 255, 0.4)';
        ctx.stroke();
        ctx.restore();
      }
    },
  };

  const tooltipPlugin = {
    id: 'tooltipPlugin',
    tooltip: {
      callbacks: {
        label: function (context: any) {
          let label = context.dataset.label || '';
          if (label) { label += ': '; }
          if (context.parsed.y !== null) {
            label += currency.format(context.parsed.y);
          }
          return label;
        },
        afterLabel: function (context: any) {
          const equityAndLtvLabel = "Equity";
          if (context.dataset.label === equityAndLtvLabel) {
            // For the Equity and LTV dataset, display both equity and LTV in the tooltip
            const i = context.dataIndex;
            const d = datasets.find(dataset => equityAndLtvLabel === dataset.name);
            if (d) {
              const ltvValue = timeScale === 'monthly' ? d.totalInterestPaidPerMonth[i] : d.totalInterestPaidPerYear[i];
              if (ltvValue !== undefined) { // Ensure ltvValue is not undefined
                const ltv = `LTV: ${ltvValue.toFixed(2)}%`;
                return ltv;
              }
            }
            return ''
          } else {
            // Mortgage dataset tooltip
            const mortgageLabels = ["Mortgage", "2nd Mortgage", "Mortgage with Offset", "Mortgage with Overpayment", "Mortgage with Offset and Overpayment"];
            const labelIndex = mortgageLabels.indexOf(context.dataset.label);
            if (labelIndex !== -1) {
              const i = context.dataIndex;
              const d = datasets.find(dataset => dataset.name === context.dataset.label);
              let noData = false;

              let interestAdded = "    Interest Added: ";
              let amountPaid = "    Amount Paid: ";
              let totalInterest = "    Total Interest Paid: ";
              let totalPaid = "    Total Paid: ";
              let titleLength = 0;
              let mortgageTypeLabel

              if (d) {
                if (!isNaN(d.interestAddedMonthly[i])) {
                  noData = true;
                }
                if (timeScale === 'monthly') {
                  interestAdded += currency.format(d.interestAddedMonthly[i]);
                  amountPaid += currency.format(d.amountPaidMonthly[i]);
                  totalInterest += currency.format(d.totalInterestPaidPerMonth[i]);
                  totalPaid += currency.format(d.totalAmountPaidPerMonth[i]);
                } else {
                  interestAdded += currency.format(d.interestAddedYearly[i]);
                  amountPaid += currency.format(d.amountPaidYearly[i]);
                  totalInterest += currency.format(d.totalInterestPaidPerYear[i]);
                  totalPaid += currency.format(d.totalAmountPaidPerYear[i]);
                }
                titleLength = d.totalAmountPaidPerYear[d.totalAmountPaidPerYear.length - 1].toString().length;
                  mortgageTypeLabel = d.name
              }
              let title = `${mortgageTypeLabel} Details:`;
              let details = [title, interestAdded, amountPaid, totalInterest, totalPaid];

              // Add a divider if it's the 1st Mortgage, for comparison
              if (labelIndex === 0) {
                const dividerLength = title.length + titleLength + 13;
                details.push("_".repeat(dividerLength));
              }
              return noData ? details.join("\n") : "";
            }
          }
        }
      },
      bodyFont: {
        weight: 'bold',
      },
    },
  };

  var plugins = [tooltipPlugin, backgroundPlugin, hoverLinePlugin] as any

  var options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...tooltipPlugin as any,
      legend: {
        labels: {
          boxWidth: 40,
          boxHeight: 0,
          font: {
            weight: 'bold'
          },
          borderRadius: 1,
          useBorderRadius: true,
        },
      },
    },
    interaction: {
      mode: 'index' as any,
      intersect: false,
    },
    elements: {
      point: {
        radius: 0, // removes points on line graph
      },
    },
    scales: {
      x: {
        type: 'time' as any,
        time: {
          tooltipFormat: timeScale === 'monthly' ? 'MMM yyyy' : 'yyyy',
          unit: timeScale === 'monthly' ? 'month' : 'year',
        },
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          maxTicksLimit: 10,
        },
      },
      y: {
        position: 'left',
        type: 'linear' as any,
        title: {
          display: true,
          text: 'Amount (£)',
        },
      },
    },
  };

  const [data, setData] = useState<{
    labels: number[];
    datasets: ChartDataset<'line'>[];
  }>({
    labels: [],
    datasets: [],
  });


  useEffect(() => {
    const hasData = datasets.length > 0;

    // Determine the maximum length of datasets
    const maxLength = datasets.reduce((max, ds) => {
      const length = timeScale === 'monthly' ? ds.monthlyData.length : ds.yearlyData.length;
      return length > max ? length : max;
    }, 0);

    // Generate labels starting from today's date
    const labels = [];
    const initialDate = startDate ? moment(startDate) : moment();
    for (let i = 0; i < maxLength; i++) {
      const labelDate = initialDate.clone().add(i, timeScale === 'monthly' ? 'months' : 'years');
      labels.push(labelDate.valueOf()); // Store as timestamp or format as needed
    }

    // Prepare datasets for the chart, selects monthly or yearly data
    let tempDataset: ChartDataset<'line'>[] = hasData ? datasets.map(dataset => {
      let adjustedData = dataset[timeScale === 'monthly' ? 'monthlyData' : 'yearlyData'];
      // If the dataset is shorter than the longest one, pad it with 0s
      if (adjustedData.length < maxLength) {
        adjustedData = [...adjustedData, ...Array(maxLength - adjustedData.length).fill(0)];
      }

      let customLineStyle; // For dashes, etc
      if (dataset.name === "Mortgage" || dataset.name === "2nd Mortgage" || dataset.name === "Mortgage with Offset" || dataset.name === "Mortgage with Overpayment" || dataset.name === "House Price"  || dataset.name === "Mortgage with Offset and Overpayment") {
        customLineStyle = { borderDash: [], borderWidth: 2 };
      } else if (dataset.name === "Mortgage without Offset" || dataset.name === "2nd Mortgage without Offset") {
        customLineStyle = { borderDash: [10, 5], borderWidth: 2 };
      } else if (dataset.name === "Mortgage without Overpayment" || dataset.name === "2nd Mortgage without Overpayment") {
        customLineStyle = { borderDash: [5, 5], borderWidth: 2 };
      } else if (dataset.name === "Offset Savings (Mortgage)" || dataset.name === "Offset Savings (2nd Mortgage)") {
        customLineStyle = { borderDash: [5, 10], borderWidth: 2 };
      } else {
        customLineStyle = { borderDash: [2, 2], borderWidth: 2 };
      }

      return {
        label: dataset.name,
        backgroundColor: dataset.colour,
        borderColor: dataset.colour,
        ...customLineStyle,
        yAxisID: 'y',
        data: adjustedData,
      };
    }) : [{ // Provide a dummy dataset with no data points if there's no data
      label: '',
      data: [],
      backgroundColor: 'rgba(0,0,0,0)',
      borderColor: 'rgba(0,0,0,0)',
    }];

    setData({
      labels,
      datasets: tempDataset,
    });
  }, [datasets, timeScale, startDate]);

  const handleChartTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setChartType(event.target.value as keyof ChartTypeRegistry);
  };

  const handleTimeScaleChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setTimeScale(event.target.value);
  };

  const handleExportChart = () => {
    if (chartRef.current) {
      const link = document.createElement('a');
      link.download = 'chart.jpeg';
      link.href = chartRef.current.toBase64Image();
      link.click();
    }
  };

  return (
    <>
      <div className="" style={{ height: '53vh' }}>
        <Chart type={chartType} data={data} plugins={plugins} options={options} ref={chartRef} />
      </div>
      <div className="flex items-end space-x-2">
        <div className="">
          <label className="block mb-2 text-sm font-medium">Choose Chart Type:</label>
          <select
            value={chartType}
            onChange={handleChartTypeChange}
            className="select select-bordered w-full max-w-xs"
          >
            <option disabled selected value={''}>
              Select your chart type
            </option>
            {chartTypeOptions.map((option) => (
              <option value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="">
          <label className="block mb-2 text-sm font-medium">Choose Time Scale:</label>
          <select
            value={timeScale}
            onChange={handleTimeScaleChange}
            className="select select-bordered w-full max-w-xs"
          >
            <option disabled selected value={''}>
              Select your time scale
            </option>
            {timeScaleOptions.map((option) => (
              <option value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center">
          <button
            className="text-l font-semibold p-3 btn-accent rounded-full flex items-center justify-center h-full"
            onClick={handleExportChart}
          >
            Export chart as image
            <FontAwesomeIcon icon={faChartSimple} className="ml-2" />
          </button>
        </div>
      </div>
    </>
  );
}

export default ChartModeller;